const DRAG_CLASSES = ['border-purple-500', 'bg-purple-900/20'];
const DELETE_CLASSES = ['border-red-700', 'bg-red-900/20'];
const IMAGE_PREVIEW_CLASS = 'mx-auto max-h-72 rounded-md shadow-lg';

/**
 * Initializes image preview fields with drag-and-drop support
 * @param {HTMLElement} parent - Parent element to search within
 */
export function init(parent) {
    parent.querySelectorAll('.image-preview-field').forEach((field) => {
        if (field.dataset.initialized === 'true') return;
        field.dataset.initialized = 'true';

        const elements = getImageFieldElements(field);
        if (!elements.fileInput || !elements.container) return;

        const state = {
            deleted: false,
            originalPreview: elements.container.querySelector('img')?.cloneNode(true)
        };

        attachImageFieldEvents(elements, state);
    });
}

/**
 * Retrieves all necessary DOM elements for an image field
 */
function getImageFieldElements(field) {
    const deleteCheckbox = field.querySelector('input[type="checkbox"]');
    const deleteLabel = deleteCheckbox?.closest('label');

    return {
        fileInput: field.querySelector('input.file-input'),
        container: field.querySelector('div.relative'),
        deleteCheckbox,
        deleteLabel,
        deleteText: deleteLabel?.querySelector('.delete-text'),
        deleteIcon: deleteLabel?.querySelector('i')
    };
}

/**
 * Attaches all event listeners to image field elements
 */
function attachImageFieldEvents(elements, state) {
    const {fileInput, container, deleteCheckbox} = elements;

    fileInput?.addEventListener('change', () => {
        handleFileSelect(fileInput.files[0], container, state, elements);
    });

    setupDragAndDrop(container, fileInput, state, elements);

    deleteCheckbox?.addEventListener('change', () => {
        deleteCheckbox.checked
            ? showDeletedState(elements, state)
            : restoreOriginalImage(elements, state);
    });
}

/**
 * Handles file selection via input or drag-and-drop
 */
function handleFileSelect(file, container, state, elements) {
    if (!file || !file.type.startsWith('image/')) return;

    if (state.deleted) {
        restoreOriginalImage(elements, state);
    }

    displayImagePreview(file, container);
}

/**
 * Displays image preview in container
 */
function displayImagePreview(file, container) {
    const reader = new FileReader();
    reader.onload = (event) => {
        container.innerHTML = `
            <img
                src="${event.target.result}"
                class="${IMAGE_PREVIEW_CLASS}"
            >
        `;
    };
    reader.readAsDataURL(file);
}

/**
 * Sets up drag-and-drop functionality
 */
function setupDragAndDrop(container, fileInput, state, elements) {
    container.addEventListener('dragover', (event) => {
        event.preventDefault();
        container.classList.add(...DRAG_CLASSES);
    });

    container.addEventListener('dragleave', (event) => {
        event.preventDefault();
        container.classList.remove(...DRAG_CLASSES);
    });

    container.addEventListener('drop', (event) => {
        event.preventDefault();
        container.classList.remove(...DRAG_CLASSES);

        const file = event.dataTransfer.files[0];
        if (!file || !file.type.startsWith('image/')) return;

        updateFileInput(fileInput, file);
        displayImagePreview(file, container);

        if (state.deleted) {
            restoreOriginalImage(elements, state);
        }
    });
}

/**
 * Updates file input with dropped file
 */
function updateFileInput(fileInput, file) {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    fileInput.files = dataTransfer.files;
}

/**
 * Shows the deleted state UI
 */
function showDeletedState(elements, state) {
    const {container, deleteCheckbox, deleteIcon, deleteText} = elements;

    state.deleted = true;
    deleteCheckbox.checked = true;

    container.innerHTML = `
        <div class="flex flex-col items-center gap-3 text-red-400">
            <i class="fa-solid fa-trash text-4xl"></i>
            <p class="text-sm font-medium">
                Image marquée pour suppression
            </p>
        </div>
    `;
    container.classList.add(...DELETE_CLASSES);

    deleteIcon.className = 'fa-solid fa-rotate-left';
    deleteText.textContent = 'Restaurer l\'image';
}

/**
 * Restores the original image
 */
function restoreOriginalImage(elements, state) {
    const {container, fileInput, deleteCheckbox, deleteIcon, deleteText} = elements;

    state.deleted = false;
    deleteCheckbox.checked = false;
    fileInput.value = '';

    container.innerHTML = '';
    if (state.originalPreview) {
        container.appendChild(state.originalPreview.cloneNode(true));
    }
    container.classList.remove(...DELETE_CLASSES);

    deleteIcon.className = 'fa-solid fa-trash';
    deleteText.textContent = 'Supprimer l\'image';
}